import { useId, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errorMessage } from "@/lib/api";
import { PRAG_UPOZORENJA } from "@/lib/status";
import { datum } from "@/lib/format";

export interface ArchivePresentation {
  trajno?: boolean;
  display_title?: string | null;
  archive_category?: string;
  naziv?: string;
  document_type_name?: string | null;
  original_filename?: string | null;
  created_at?: string | null;
  issued_on?: string | null;
  expires_on?: string | null;
}

export function archiveTitle(document: ArchivePresentation): string {
  if (document.display_title) return document.display_title;
  const name = document.naziv;
  if (name && name !== document.original_filename && !/\.(pdf|jpe?g|png|tiff?|heic|docx?|xlsx?)$/i.test(name)) return name;
  const type = document.document_type_name || "Dokument";
  return document.expires_on ? `${type} — vrijedi do ${datum(document.expires_on)}` : document.issued_on ? `${type} — izdano ${datum(document.issued_on)}` : document.created_at ? `${type} — ${datum(document.created_at)}` : type;
}

const CATEGORIES = ["Identitet", "Dozvole", "Zdravlje", "ZNR", "Ugovori i prijave", "Ostalo"];
export function ArchiveGroups<T extends ArchivePresentation>({ documents, children }: { documents: T[]; children: (rows: T[]) => ReactNode }) {
  return <div className="space-y-3">{CATEGORIES.map(category => {
    const rows = documents.filter(document => (CATEGORIES.includes(document.archive_category || "") ? document.archive_category : "Ostalo") === category);
    return rows.length ? <details key={category} open className="min-w-0 rounded border border-border">
      <summary className="cursor-pointer rounded bg-muted px-3 py-2 font-semibold focus-visible:outline focus-visible:outline-2">{category} <span className="font-normal text-muted-foreground">({rows.length})</span></summary>
      {children(rows)}
    </details> : null;
  })}</div>;
}

export function ArchiveMetadata({ document, verified, historical = false, deleted = false }: { document: ArchivePresentation; verified: boolean; historical?: boolean; deleted?: boolean }) {
  const today = new Date();
  const days = !document.trajno && document.expires_on ? Math.round((Date.parse(document.expires_on.slice(0, 10)) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000) : null;
  return <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
    <span>Izdano {document.issued_on ? datum(document.issued_on) : "—"}</span>
    {document.trajno && <Badge ton="ok">Trajno</Badge>}
    {deleted ? <Badge>Obrisan</Badge> : historical ? <Badge>Povijesno</Badge> : <>
      <Badge ton={verified ? "ok" : "idle"}>{verified ? "Provjeren" : "Nije provjeren"}</Badge>
      {days !== null && days < 0 ? <Badge ton="bad">Istekao</Badge> : days !== null && days <= PRAG_UPOZORENJA ? <Badge ton="warn">Istječe</Badge> : null}
    </>}
  </div>;
}

export function ArchiveTitleEditor({ title, save }: { title: string; save: (value: string) => Promise<unknown> }) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!editing) return <Button variant="secondary" size="sm" onClick={() => { setValue(title); setError(null); setEditing(true); }}>Uredi naslov</Button>;
  return <form className="space-y-2" onSubmit={event => {
    event.preventDefault(); if (!value.trim() || busy) return;
    setBusy(true); setError(null);
    void save(value.trim()).then(() => setEditing(false)).catch((cause: unknown) => setError(errorMessage(cause, "Naslov nije spremljen."))).finally(() => setBusy(false));
  }}>
    <label htmlFor={id}>Naslov dokumenta</label><Input id={id} required maxLength={500} value={value} disabled={busy} onChange={event => setValue(event.target.value)} />
    {error && <p role="alert">{error}</p>}
    <div className="flex flex-wrap gap-2"><Button type="submit" disabled={busy || !value.trim()}>Spremi naslov</Button><Button type="button" variant="secondary" disabled={busy} onClick={() => setEditing(false)}>Odustani</Button></div>
  </form>;
}

export function ArchiveTitleButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className="max-w-full text-left font-medium underline underline-offset-2" style={{ overflowWrap: "anywhere", whiteSpace: "normal" }} {...props} />;
}
